/**
 * Re-exports the canonical WidgetPrefillData type from the public SDK package
 * (@ridereserv/widget-react) so that apps/web and widget-react share a single
 * definition and cannot drift apart.
 *
 * The validator below is web-only (runtime validation of untrusted postMessage
 * payloads). The type contract itself lives in the SDK so that operators
 * consuming @ridereserv/widget-react get the same shape that the iframe expects.
 *
 * SECURITY: Raw incoming messages must always pass through
 * {@link validatePrefillData} before use. Never access event.data.data directly.
 */
export type { WidgetPrefillData } from "@ridereserv/widget-react";

import type { WidgetPrefillData } from "@ridereserv/widget-react";

/**
 * Validate and sanitize a raw (untrusted) postMessage payload into a
 * {@link WidgetPrefillData} object, or return null if it is unusable.
 *
 * Rules applied per field:
 * - All string fields: must be a string, length-capped (excess silently trimmed
 *   to cap — a truncated address is better than a silent no-op that confuses
 *   operators).
 * - pickupDatetime: must parse as a valid date.
 * - passengerCount: must be an actual integer number, clamped 1–20.
 * - luggageCount: must be an actual integer number, clamped 0–20.
 * - email: lightweight sanity check (one local part, one "@", one domain) —
 *   the booking schema performs the authoritative validation downstream.
 *
 * Returns null only when raw is not an object (completely unusable), so that
 * a partial payload with some valid fields still partially prefills the form.
 *
 * SECURITY: This is the single inbound trust boundary for prefill data — it
 * rejects unexpected types (only known keys are copied, so no prototype
 * pollution) and clamps/caps every value. It is intentionally lightweight,
 * not authoritative: the booking schema (client + server) is what ultimately
 * validates the data. Never use raw message payloads directly — always call
 * this function first.
 *
 * @param raw - Untrusted value from event.data.data
 * @returns Validated WidgetPrefillData or null
 */
export function validatePrefillData(raw: unknown): WidgetPrefillData | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }

  const input = raw as Record<string, unknown>;
  const result: WidgetPrefillData = {};

  // ── String fields with length caps ────────────────────────────────────────

  if (typeof input["pickupAddress"] === "string") {
    result.pickupAddress = input["pickupAddress"].slice(0, 255);
  }

  if (typeof input["dropoffAddress"] === "string") {
    result.dropoffAddress = input["dropoffAddress"].slice(0, 255);
  }

  if (typeof input["passengerFirstName"] === "string") {
    result.passengerFirstName = input["passengerFirstName"].slice(0, 100);
  }

  if (typeof input["passengerLastName"] === "string") {
    result.passengerLastName = input["passengerLastName"].slice(0, 100);
  }

  if (typeof input["phone"] === "string") {
    result.phone = input["phone"].slice(0, 20);
  }

  // ── Email: lightweight sanity check, max 254 ──────────────────────────────
  // Sanity only — one local part, one "@", one domain, no whitespace. The
  // booking schema (client + server) performs the authoritative validation.

  if (typeof input["email"] === "string") {
    const trimmed = input["email"].slice(0, 254);
    if (/^[^@\s]+@[^@\s]+$/.test(trimmed)) {
      result.email = trimmed;
    }
  }

  // ── pickupDatetime: must parse as a valid date ─────────────────────────────

  if (typeof input["pickupDatetime"] === "string") {
    const truncated = input["pickupDatetime"].slice(0, 50); // reasonable ISO max
    if (!isNaN(Date.parse(truncated))) {
      result.pickupDatetime = truncated;
    }
  }

  // ── Numeric fields ─────────────────────────────────────────────────────────
  // Require an actual integer NUMBER. Number() coercion is too lenient for an
  // untrusted payload — Number("") === 0, Number("3") === 3, Number([5]) === 5
  // would all sneak strings/arrays through as valid counts.

  const passengerCount = input["passengerCount"];
  if (
    typeof passengerCount === "number" &&
    Number.isInteger(passengerCount) &&
    passengerCount >= 1 &&
    passengerCount <= 20
  ) {
    result.passengerCount = passengerCount;
  }

  const luggageCount = input["luggageCount"];
  if (
    typeof luggageCount === "number" &&
    Number.isInteger(luggageCount) &&
    luggageCount >= 0 &&
    luggageCount <= 20
  ) {
    result.luggageCount = luggageCount;
  }

  return result;
}
